import { Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { map, filter, debounceTime, take, timeout } from "rxjs/operators";
import { Query } from "@datorama/akita";
import {
  IDefinition,
  IDefinitionState,
} from "@interfaces/definition.interface";

import { DefinitionsStore } from "./definitions.store";

@Injectable({ providedIn: "root" })
export class DefinitionsQuery extends Query<IDefinitionState> {
  constructor(protected store: DefinitionsStore) {
    super(store);
  }

  unitConfigs$ = this.select((state) => state.unitConfigs);

  public latestFile(ecuId: number, compVersion: number): IDefinition | null {
    const files = this.store
      .getValue()
      .unitConfigs.filter(
        (x) => x.ecuId === ecuId && x.compVersion === compVersion,
      );
    //console.log('files', files);

    if (files?.length > 0) {
      return files.reduce((a, b) => (a.buildVersion > b.buildVersion ? a : b));
    } else {
      console.log("returning null");
      return null;
    }
  }

  public async latestFileAsync(
    ecuId: number,
    compVersion: number,
    settleMs = 150, // wait for no changes for this long
    waitMs = 5000, // overall timeout
  ): Promise<IDefinition | null> {
    const files = await firstValueFrom(
      this.unitConfigs$.pipe(
        map((list) =>
          list.filter(
            (x) => x.ecuId === ecuId && x.compVersion === compVersion,
          ),
        ),
        filter((arr) => arr.length > 0), // wait until at least one match exists
        debounceTime(settleMs), // “settling” period: no updates for N ms
        take(1),
        timeout({ each: waitMs }),
      ),
    );

    // numeric compare; switch to semver compare if needed
    return files.reduce((a, b) =>
      Number(a.buildVersion) > Number(b.buildVersion) ? a : b,
    );
  }

  public compatibleFile(
    ecuId: number,
    buildNumber: number,
    compVersion: number,
  ): IDefinition | null {
    const file = this.store
      .getValue()
      .unitConfigs.find(
        (x) => x.ecuId === ecuId && x.buildNumber === buildNumber,
      );
    if (file) {
      return file;
    } else {
      return this.latestFile(ecuId, compVersion);
    }
  }

  public file(ecuId: number, compVersion: number): IDefinition | null {
    var item = this.store
      .getValue()
      .unitConfigs.find(
        (x) => x.ecuId === ecuId && x.compVersion === compVersion,
      );
    if (!item) {
      return null;
    }
    return item;
  }

  public fileAsObservable(ecuId: number, compVersion: number) {
    return this.select((state) =>
      state.unitConfigs.find(
        (x) => x.ecuId === ecuId && x.compVersion === compVersion,
      ),
    );
  }
}
